/**
 * Payments are accepted on Solana L1 and Ethereum L1 — no L2s, no other chains.
 * Both are first-class: each has its own escrow contract and its own adapter
 * behind the shared `ChainAdapter` interface (see apps/api/src/chain).
 */
export const CHAINS = ['solana', 'ethereum'] as const;
export type Chain = (typeof CHAINS)[number];

export const CHAIN_META: Record<Chain, {
  label: string;
  namespace: 'solana' | 'eip155';
  /** USDC is 6dp on both chains. */
  feeDecimals: number;
  /** Reference chain id: mainnet-beta genesis for Solana, EIP-155 id for Ethereum. */
  reference: string;
  enabled: boolean;
}> = {
  solana: {
    label: 'Solana',
    namespace: 'solana',
    feeDecimals: 6,
    reference: '5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp',
    enabled: true,
  },
  ethereum: {
    label: 'Ethereum',
    namespace: 'eip155',
    feeDecimals: 6,
    reference: '1',
    enabled: true,
  },
};

export function isEnabledChain(c: string): c is Chain {
  return (CHAINS as readonly string[]).includes(c) && CHAIN_META[c as Chain].enabled;
}

/**
 * Ethereum L1 gas makes settlement materially more expensive than on Solana: a
 * fund/release pair can cost several dollars at busy times, which is a large
 * bite out of a $10 inbox fee. The UI surfaces an estimate before funding so a
 * sender picking Ethereum is choosing it with the cost in front of them.
 */
export const SETTLEMENT_COST_NOTE: Record<Chain, 'negligible' | 'significant'> = {
  solana: 'negligible',
  ethereum: 'significant',
};

/** Block explorer links, so a funded escrow can be checked against the chain. */
export function explorerTxUrl(chain: Chain, signature: string, cluster?: string): string {
  if (chain === 'solana') {
    const q = cluster && cluster !== 'mainnet-beta' ? `?cluster=${cluster}` : '';
    return `https://solscan.io/tx/${signature}${q}`;
  }
  return `https://etherscan.io/tx/${signature}`;
}

export function explorerAddressUrl(chain: Chain, address: string, cluster?: string): string {
  if (chain === 'solana') {
    const q = cluster && cluster !== 'mainnet-beta' ? `?cluster=${cluster}` : '';
    return `https://solscan.io/account/${address}${q}`;
  }
  return `https://etherscan.io/address/${address}`;
}

/**
 * Which chain an identifier belongs to, from its shape alone. Lets the compose
 * screen drop its chain selector: `0x…` and `.eth` are Ethereum, base58 and
 * `.sol` are Solana, and asking the user to restate it invites mismatches.
 */
export function inferChain(input: string): Chain | null {
  const v = input.trim();
  if (/^0x[a-fA-F0-9]{40}$/.test(v)) return 'ethereum';
  if (/\.eth$/i.test(v)) return 'ethereum';
  if (/\.sol$/i.test(v)) return 'solana';
  if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(v)) return 'solana';
  return null;
}

export function isName(input: string): boolean {
  return /\.(eth|sol)$/i.test(input.trim());
}

/** A wallet address is only ever meaningful together with its chain. */
export interface WalletRef {
  chain: Chain;
  address: string;
}

export function walletKey(w: WalletRef): string {
  return `${w.chain}:${w.address}`;
}
